import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  createCountryList,
  getCountryForValue,
  getCountryForCode,
  getDefaultCountry,
  getSortedCountryList,
  normalizePhoneForMatching,
  shouldPreserveCountry,
  Country,
} from 'utils/country';
import { formatPhoneNumber } from 'utils/phoneNumber';
import PhoneInput from '../../atoms/PhoneInput/PhoneInput';
import CountrySelector from '../../atoms/CountrySelector/CountrySelector';
import { PhoneNumberContainer } from './styles';

interface PhoneNumberInputProps {
  value?: string;
  onChange: (value: string) => void;
  countryCode?: string;
  countryCodeEditable?: boolean;
  countrySelectorProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;

  onTelephoneCountryCodeChange: (countryCode: string) => void;
  telephoneCountryCode: string;
}

const PhoneNumberInput = ({
  countryCode,
  countryCodeEditable = true,
  countrySelectorProps = {},
  inputProps = {},
  inputRef,
  onChange,
  onTelephoneCountryCodeChange,
  telephoneCountryCode,
  value,
}: PhoneNumberInputProps) => {
  const countryList = useMemo(() => createCountryList(), []);
  const sortedCountryList = useMemo(
    () => getSortedCountryList(countryList),
    [countryList],
  );
  const US_COUNTRY = useMemo(
    () => getDefaultCountry(countryList),
    [countryList],
  );

  const getCountryForValueCallback = useCallback(
    (val?: string) => getCountryForValue(val, sortedCountryList),
    [sortedCountryList],
  );

  const getCountryForCodeCallback = useCallback(
    (code?: string) => getCountryForCode(code, countryList),
    [countryList],
  );

  const initialCountry =
    getCountryForCodeCallback(countryCode) ||
    getCountryForValueCallback(value) ||
    US_COUNTRY;

  // Always use formatted value for the input
  const initialInputValue =
    value && value !== '+'
      ? formatPhoneNumber(value, initialCountry.iso2)
      : formatPhoneNumber(initialCountry.dialCode, initialCountry.iso2);

  const [selectedCountry, setSelectedCountry] =
    useState<Country>(initialCountry);

  if (!telephoneCountryCode)
    onTelephoneCountryCodeChange(
      selectedCountry.dialCode || initialCountry.dialCode || '',
    );

  const [phoneNumber, setPhoneNumber] = useState(initialInputValue);

  useEffect(() => {
    if (!value) {
      // Only if value is truly empty, fill with default
      const formatted = formatPhoneNumber(
        selectedCountry.dialCode,
        selectedCountry.iso2,
      );
      setPhoneNumber(formatted);
      onChange(selectedCountry.dialCode);
      return;
    }
    const matchedCountry = getCountryForValueCallback(value);
    const normalizedValue = normalizePhoneForMatching(value);
    // Preserve current country when value is ambiguous (e.g. "+1" with TC selected)
    const countryToUse =
      !matchedCountry || shouldPreserveCountry(normalizedValue, selectedCountry)
        ? selectedCountry
        : matchedCountry;
    if (countryToUse.iso2 !== selectedCountry.iso2) {
      setSelectedCountry(countryToUse);
      onTelephoneCountryCodeChange(countryToUse.dialCode);
    }
    setPhoneNumber(formatPhoneNumber(value, countryToUse.iso2));
  }, [
    value,
    selectedCountry,
    getCountryForValueCallback,
    onChange,
    onTelephoneCountryCodeChange,
  ]);

  const handleCountryChange = (newCountry: Country) => {
    setSelectedCountry(newCountry);
    onTelephoneCountryCodeChange(newCountry.dialCode);
    const formatted = formatPhoneNumber(newCountry.dialCode, newCountry.iso2);
    setPhoneNumber(formatted);
    onChange(newCountry.dialCode);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value;

    if (userInput === '') {
      // If the user deletes everything, reset to default country code
      const formatted = formatPhoneNumber(
        selectedCountry.dialCode,
        selectedCountry.iso2,
      );
      setPhoneNumber(formatted);
      onChange(selectedCountry.dialCode);
      onTelephoneCountryCodeChange(selectedCountry.dialCode);
      return;
    }

    if (userInput === '+') {
      // Allow just '+', don't auto-fill
      setPhoneNumber('+');
      onChange('+');
      return;
    }

    const matchedCountry = getCountryForValueCallback(userInput);
    const normalizedInput = normalizePhoneForMatching(userInput);
    const shouldSwitch =
      matchedCountry &&
      matchedCountry.iso2 !== selectedCountry.iso2 &&
      !shouldPreserveCountry(normalizedInput, selectedCountry);
    if (shouldSwitch) {
      setSelectedCountry(matchedCountry);
      onTelephoneCountryCodeChange(matchedCountry.dialCode);
    }
    const formattedPhoneNumber = formatPhoneNumber(
      userInput,
      (matchedCountry || selectedCountry).iso2,
    );
    setPhoneNumber(formattedPhoneNumber);
    onChange(formattedPhoneNumber);
  };

  return (
    <PhoneNumberContainer>
      <CountrySelector
        countries={countryList}
        selectedCountry={selectedCountry}
        onCountryChange={handleCountryChange}
        disabled={!countryCodeEditable}
        {...countrySelectorProps}
      />
      <PhoneInput
        value={phoneNumber}
        onChange={handlePhoneChange}
        ref={inputRef}
        {...inputProps}
      />
    </PhoneNumberContainer>
  );
};

export default PhoneNumberInput;
